
import Vue from 'vue'
import * as Papa from 'papaparse'
import * as Helpers from '@/services/SchoolStudentHelpers'
import * as Api from '@/api/SchoolSettingsApi'
import { PageHeader } from '../components'
import { Dropzone } from '@/components'
import { SaveButton } from '@/components/buttons'
import { MultiSelect } from '@/components/formGroups'
import { DatePicker } from '@/components/input'
import {ClassRoomDto} from "@ViewModels/ClassRoomDto";

export default Vue.extend({
   name: 'StudentUpload',
   props: {
      settings: { type: Boolean, default: false}
   },
   data() {
      return {
         file: null as File | null,
         items: [] as string[][],
         headers: [] as any[],
         hasHeaderRow: true,
         IGNORE: 'Ignore',

         allowedExtensions: ['.csv'],
         parseErrors: [] as string[],
         errorMessage: '' as string,

         loading: true,
         completedCount: 0,
         uploading: false,
         uploaded: false,

         selectedClassRoomIds: [] as string[],
         classroomOptions: [] as NameValuePair[],
      }
   },
   computed: {
      activeSchoolId(): string {
        return this.$store.getters.activeSchoolId
      },
      activeSchoolYear(): number {
        return this.$store.getters.schoolYearFilter
      },
      hasFile(): boolean {
         return this.file !== null
      },
      filename(): string {
         return this.file !== null ? this.file.name : ''
      },
      selectedClassNames(): string {
         if (this.selectedClassRoomIds.length === 0) {
            return '[no classes selected!]'
         }
         return this.selectedClassRoomIds
            .map((classId: string) => {
               const match = this.classroomOptions.find(opt => opt.value === classId)
               return match ? match.name : ''
            })
            .join(', ')
      },
      buttonCaption(): string {
         return `Upload ${this.itemsWithoutHeader.length} Student${
            this.itemsWithoutHeader.length === 1 ? '' : 's'
         } in ${this.selectedClassNames}`
      },
      canUpload(): boolean {
         return (
            this.selectedClassRoomIds.length > 0 &&
            this.hasFile &&
            this.validationErrors.length === 0 &&
            this.parseErrors.length === 0 &&
            !this.uploaded
         )
      },
      selectedSchoolYear(): number {
         return this.$store.getters.selectedSchoolYear
      },
      normalisedItems(): string[][] {
         const data: string[][] = []
         const headerRow = this.headers.filter(h => h.value !== this.IGNORE)
         data.push(headerRow.map(h => h.value))
         this.items.forEach((item, index) => {
            if (index === 0 && this.hasHeaderRow) {
               return
            }
            const row: string[] = []
            headerRow.forEach(header => {
               row.push(item[header.index])
            })
            if (row.length > 0) {
               data.push(row)
            }
         })
         return data
      },
      itemsWithoutHeader(): string[][] {
         if (this.hasHeaderRow) {
            return this.items.slice(1, this.items.length)
         } else {
            return this.items
         }
      },
      validationErrors(): string[] {
         return Helpers.getValidationErrors(this.headers, this.itemsWithoutHeader, this.IGNORE)
      },
      validationWarnings(): string[] {
         return Helpers.getValidationWarnings(this.headers, this.itemsWithoutHeader, this.IGNORE)
      },
      parsedHeaders(): string[] {
         const firstRow = this.items.first
         if (!firstRow) {
            return []
         }
         if (this.hasHeaderRow) {
            return firstRow
         } else {
            const labels = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
            const letters = labels.slice(0, firstRow.length).split('')
            return letters.map(letter => `Column ${letter}`)
         }
      },
      parsedItems(): string[][] {
         if (this.hasHeaderRow) {
            return this.items.slice(1, this.items.length)
         } else {
            return this.items
         }
      },
      headerOptions(): string[] {
         const headerNames = Helpers.headerSpecs.map(s => s.name)
         headerNames.unshift(this.IGNORE)
         return headerNames
      },
      crumbs(): Breadcrumb[] {
         return [
            {
               text: 'Students',
               route: 'student-list',
               params: { schoolId: this.activeSchoolId },
            },
            { text: 'Upload Students' },
         ]
      },
      csv(): string {
         return Papa.unparse(this.normalisedItems)
      },
   },
   mounted() {
      this.selectedClassRoomIds = this.$store.getters.classroomIdsFilter
      this.loadClassroomOptions()
      this.loading = false
   },
   methods: {
       async loadClassroomOptions() {
         const classrooms = await Api.getClassroomsForYear(this.activeSchoolId, this.activeSchoolYear)
         this.classroomOptions = classrooms
             .filter((item: ClassRoomDto) => +item.SchoolYear === +this.activeSchoolYear)
             .map(
                 (item: ClassRoomDto) =>
                     ({
                       name: item.Name,
                       value: item.Id,
                     } as NameValuePair)
             )
             .sort((a: NameValuePair, b: NameValuePair) => a.name.localeCompare(b.name))
       },
      async upload() {
         try {
            this.uploading = true
            const result = await Api.saveSchoolStudentsCsv(this.activeSchoolId, this.csv, this.selectedClassRoomIds)
            this.uploaded = true
            this.completedCount = (result.Data as any).StudentsAdded
         } finally {
            this.uploading = false
         }
      },
      parseCsv() {
         const config = {
            worker: false,
            complete: (result: any) => {
               this.items = result.data.filter((row: string[]) => row.join('').trim() !== '')
               this.parseErrors = result.errors
               this.headers = Helpers.createHeaders(this.parsedHeaders, this.IGNORE)
            },
            error: (result: any) => {
               this.errorMessage = result
            },
            skipEmptyLines: true,
         }
         Papa.parse(this.file, config)
      },
      removeItem(item: string[]) {
         this.items.splice(this.items.indexOf(item), 1)
      },
      columnIsValid(index: number) {
         const header = this.headers[index]
         if (!header) {
            return false
         }
         return header.value !== this.IGNORE
      },
      reset() {
         this.file = null
         this.items = []
         this.headers = []
         this.uploaded = false
         this.completedCount = 0
         this.uploading = false
         this.errorMessage = ''
      },
      resetUpload() {
         this.reset()
         const input = this.$refs.fileInput as HTMLInputElement
         input.value = ''
         input.type = 'text'
         input.type = 'file'
      },
      onFileSelect(event: any) {
         this.reset()
         if (event.target.files.length === 0) {
            return
         }
         this.filesSelected(event.target.files)
      },
      filesSelected(files: FileList) {
         const file = files[0]
         const name = file.name
         const extension = name.substring(name.lastIndexOf('.'))
         const allowed = this.allowedExtensions.includes(extension)
         if (!allowed) {
            this.errorMessage = `File extension ${extension} is not allowed.`
            return
         }
         this.file = file
         this.parseCsv()
      },
   },
   components: {
      PageHeader,
      SaveButton,
      MultiSelect,
      DatePicker,
      Dropzone,
   },
})
